import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CreditCard, RotateCcw, Loader2, Download, FileText, Mail, Smartphone, Info, DollarSign, TrendingUp, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

function PaymentMethodBadge({ method }) {
  const labels = {
    apple_pay: { label: 'Apple Pay', icon: Smartphone, cls: 'bg-zinc-900 text-white dark:bg-zinc-200 dark:text-zinc-900' },
    google_pay: { label: 'Google Pay', icon: Smartphone, cls: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300' },
    card: { label: 'Card', icon: CreditCard, cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  };
  const info = labels[method] || labels.card;
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 ${info.cls}`} data-testid="payment-method-badge">
      <Icon className="w-3 h-3" /> {info.label}
    </span>
  );
}

export default function Transactions() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showFees, setShowFees] = useState(false);
  const limit = 20;

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * limit),
        limit: String(limit)
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());

      const res = await api.get(`/transactions?${params}`);
      setTransactions(res.data.transactions);
      setTotal(res.data.total);
      setSummary(res.data.summary || null);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [statusFilter, page, dateFrom, dateTo]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());

      const res = await api.get(`/transactions/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'emergencypos_transactions.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await api.post(`/transactions/${refundTarget.transaction_id}/refund`);
      toast.success('Refund processed successfully');
      setRefundTarget(null);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const handleSendReceipt = async () => {
    if (!emailTarget || !recipientEmail) return;
    setSendingEmail(true);
    try {
      await api.post('/transactions/send-receipt', {
        transaction_id: emailTarget.transaction_id,
        recipient_email: recipientEmail
      });
      toast.success(`Receipt sent to ${recipientEmail}`);
      setEmailTarget(null);
      setRecipientEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    const map = {
      paid: 'status-paid',
      initiated: 'status-pending',
      pending: 'status-pending',
      failed: 'status-failed',
      expired: 'status-failed',
      refunded: 'status-refunded'
    };
    return map[status] || 'status-pending';
  };

  const canRefund = user?.role === 'owner' || user?.role === 'admin';
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="transactions-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Transactions
        </h1>
        <p className="text-sm text-slate-500 mt-1">{total} total transactions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3" data-testid="transaction-filters">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-full sm:w-40 h-10" data-testid="status-filter">
            <SelectValue placeholder={t('transactions.allStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('transactions.allStatus')}</SelectItem>
            <SelectItem value="paid">{t('transactions.paid')}</SelectItem>
            <SelectItem value="initiated">{t('transactions.pending')}</SelectItem>
            <SelectItem value="refunded">{t('transactions.refunded')}</SelectItem>
            <SelectItem value="failed">{t('transactions.failed')}</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label className="text-xs font-bold uppercase text-slate-400 shrink-0">{t('transactions.from')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className="h-10 px-2 text-sm border border-slate-200 bg-white focus:outline-none focus:border-orange-500 flex-1 min-w-0"
            data-testid="date-from-input"
          />
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label className="text-xs font-bold uppercase text-slate-400 shrink-0">{t('transactions.to')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className="h-10 px-2 text-sm border border-slate-200 bg-white focus:outline-none focus:border-orange-500 flex-1 min-w-0"
            data-testid="date-to-input"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          disabled={exporting}
          className="h-10 w-full sm:w-auto sm:ml-auto"
          data-testid="export-csv-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          {t('transactions.exportCsv')}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className={`grid ${user?.role === 'cashier' ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-3`} data-testid="transaction-summary">
          <Card className="border-orange-200/50 bg-orange-50/30 dark:bg-orange-950/10 dark:border-orange-900/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Today</span>
              </div>
              <p className="text-xl font-bold text-orange-600 dark:text-orange-400" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="today-revenue">{summary.today_revenue.toFixed(2)} &euro;</p>
              <p className="text-[10px] text-muted-foreground">{summary.today_count} paid txn{summary.today_count !== 1 ? 's' : ''}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Period Revenue</span>
              </div>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="period-revenue">{summary.period_revenue.toFixed(2)} &euro;</p>
              <p className="text-[10px] text-muted-foreground">{summary.period_count} paid txn{summary.period_count !== 1 ? 's' : ''}</p>
            </CardContent>
          </Card>
          {user?.role !== 'cashier' && (
            <>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Zap className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Fees</span>
                  </div>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="period-fees">{(summary.period_platform_fees + summary.period_stripe_fees).toFixed(2)} &euro;</p>
                  <p className="text-[10px] text-muted-foreground">Platform: {summary.period_platform_fees.toFixed(2)} + Stripe: {summary.period_stripe_fees.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200/50 bg-emerald-50/30 dark:bg-emerald-950/10 dark:border-emerald-900/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 mb-1">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Net</span>
                  </div>
                  <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="period-net">{summary.period_net.toFixed(2)} &euro;</p>
                  <p className="text-[10px] text-muted-foreground">After all fees</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Transaction List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)}
        </div>
      ) : transactions.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No transactions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-card border border-border" data-testid="transactions-list">
          {/* Desktop Header - hidden on mobile */}
          <div className="hidden md:flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
            <div className="grid grid-cols-12 flex-1 text-xs uppercase tracking-wider font-bold text-muted-foreground">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-2 text-right pr-3">Amount</div>
              <div className="col-span-2 pl-3">Method</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
          </div>
          {/* Fee toggle */}
          {(user?.role === 'owner' || user?.role === 'admin') && (
            <button
              onClick={() => setShowFees(!showFees)}
              className="w-full flex items-center gap-1.5 px-4 py-1.5 text-[10px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors border-b border-border/50"
              data-testid="toggle-fees-col-btn"
            >
              <Info className="w-3 h-3" />
              {showFees ? 'Hide fees' : 'Show fees on each transaction'}
            </button>
          )}

          {transactions.map((txn, i) => (
            <div
              key={txn.transaction_id}
              className={`${i < transactions.length - 1 ? 'border-b border-border/50' : ''} hover:bg-muted/30`}
              data-testid={`txn-row-${txn.transaction_id}`}
            >
              {/* === MOBILE LAYOUT === */}
              <div className="md:hidden p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {txn.description || `Payment #${txn.transaction_id.slice(-6)}`}
                    </div>
                    <div className="text-xs text-muted-foreground">{txn.created_by_name || 'Staff'}</div>
                    {txn.customer_email && <div className="text-[10px] text-blue-500 truncate">{txn.customer_email}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-base font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCurrency(txn.amount)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)} {formatTime(txn.created_at)}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatusClass(txn.payment_status)}`}>{txn.payment_status}</Badge>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {txn.payment_status === 'paid' && (
                      <Button variant="ghost" size="sm" onClick={() => { setEmailTarget(txn); setRecipientEmail(''); }} className="text-muted-foreground hover:text-blue-600 h-7 w-7 p-0" data-testid={`email-btn-${txn.transaction_id}`}>
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {txn.payment_status === 'paid' && (
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/receipt/${txn.transaction_id}`)} className="text-muted-foreground hover:text-foreground h-7 w-7 p-0" data-testid={`receipt-btn-${txn.transaction_id}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {canRefund && txn.payment_status === 'paid' && txn.refund_status !== 'refunded' && (
                      <Button variant="ghost" size="sm" onClick={() => setRefundTarget(txn)} className="text-muted-foreground hover:text-red-600 h-7 w-7 p-0" data-testid={`refund-btn-${txn.transaction_id}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {showFees && txn.payment_status === 'paid' && (
                  <div className="text-[10px] text-muted-foreground tabular-nums flex gap-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <span>Platform: {txn.promo_applied ? '0.00' : (txn.platform_fee || 0).toFixed(2)}</span>
                    <span>Stripe: ~{(txn.stripe_fee_estimate || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* === DESKTOP LAYOUT === */}
              <div className="hidden md:grid grid-cols-12 px-4 py-3 items-center">
                <div className="col-span-2">
                  <div className="text-sm font-medium">{formatDate(txn.created_at)}</div>
                  <div className="text-xs text-muted-foreground">{formatTime(txn.created_at)}</div>
                </div>
                <div className="col-span-3">
                  <div className="text-sm truncate">{txn.description || `Payment #${txn.transaction_id.slice(-6)}`}</div>
                  <div className="text-xs text-muted-foreground truncate">{txn.created_by_name || 'Staff'}</div>
                  {txn.customer_email && <div className="text-[10px] text-blue-500 truncate">{txn.customer_email}</div>}
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-sm font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(txn.amount)}</span>
                  {showFees && txn.payment_status === 'paid' && (
                    <div className="mt-0.5 space-y-0 text-[10px] text-muted-foreground tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <div className="flex justify-end gap-1"><span>Platform:</span><span className={txn.promo_applied ? 'line-through' : ''}>{txn.promo_applied ? '0.00' : (txn.platform_fee || 0).toFixed(2)}</span></div>
                      <div className="flex justify-end gap-1"><span>Stripe:</span><span>~{(txn.stripe_fee_estimate || 0).toFixed(2)}</span></div>
                    </div>
                  )}
                </div>
                <div className="col-span-2"><PaymentMethodBadge method={txn.payment_method} /></div>
                <div className="col-span-1">
                  <Badge variant="outline" className={`text-xs px-2 py-0.5 ${getStatusClass(txn.payment_status)}`}>{txn.payment_status}</Badge>
                </div>
                <div className="col-span-2 text-right flex items-center justify-end gap-1">
                  {txn.payment_status === 'paid' && (
                    <Button variant="ghost" size="sm" onClick={() => { setEmailTarget(txn); setRecipientEmail(''); }} className="text-slate-400 hover:text-blue-600 h-8 px-2" data-testid={`email-btn-${txn.transaction_id}`} title="Email receipt"><Mail className="w-4 h-4" /></Button>
                  )}
                  {txn.payment_status === 'paid' && (
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/receipt/${txn.transaction_id}`)} className="text-slate-400 hover:text-slate-700 h-8 px-2" data-testid={`receipt-btn-${txn.transaction_id}`} title="View receipt"><FileText className="w-4 h-4" /></Button>
                  )}
                  {canRefund && txn.payment_status === 'paid' && txn.refund_status !== 'refunded' && (
                    <Button variant="ghost" size="sm" onClick={() => setRefundTarget(txn)} className="text-slate-500 hover:text-red-600 h-8 px-2" data-testid={`refund-btn-${txn.transaction_id}`}><RotateCcw className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
            data-testid="prev-page-btn"
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            data-testid="next-page-btn"
          >
            Next
          </Button>
        </div>
      )}

      {/* Refund Dialog */}
      <Dialog open={!!refundTarget} onOpenChange={(open) => !open && setRefundTarget(null)}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Refund</DialogTitle>
            <DialogDescription>
              Are you sure you want to refund {refundTarget && formatCurrency(refundTarget.amount)} to the customer?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefundTarget(null)} data-testid="cancel-refund-btn">
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              disabled={refunding}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-refund-btn"
            >
              {refunding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              {refunding ? 'Processing...' : 'Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Receipt Dialog */}
      <Dialog open={!!emailTarget} onOpenChange={(open) => !open && setEmailTarget(null)}>
        <DialogContent data-testid="email-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Email Receipt</DialogTitle>
            <DialogDescription>
              Send a payment receipt for {emailTarget && formatCurrency(emailTarget.amount)} to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Customer Email</Label>
              <Input
                type="email"
                placeholder="customer@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="h-11"
                data-testid="recipient-email-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button
              onClick={handleSendReceipt}
              disabled={sendingEmail || !recipientEmail}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-receipt-btn"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {sendingEmail ? 'Sending...' : 'Send Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
